import { connect } from "react-redux";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Warning } from "@styled-icons/ionicons-solid";
import { Flex, Box, Image } from "rebass/styled-components";
import { useToaster } from "@hellocontento/maillard";

import {
  Username,
  CardHead,
  CardAction,
  ServiceCard,
  CardActions,
  Instruction,
  ServiceName,
  CardMoreMenu,
  ChannelStatus,
  CardActionWrapper
} from "./services/styles";
import Button from "components/common/Button";
import moreIcon from "assets/images/more.png";
import { ChannelAvatar, Avatar, Service } from "components/common/styles";
import DefaultAvatar from "assets/images/dashboard/avatar-empty-state.png";
import PermissionsAddedModal from "../modals/permissionsAddedModal";
import { callApi } from "../../utils/ContentoApi";


const ChannelConnectionCard = props => {
  const {
    disabled,
    username,
    status,
    onConnect,
    channel,
    onDisconnect,
    squareIcon,
    avatar,
    instruction,
    account,
    connected
  } = props;
  const history = useHistory();

  const addToast = useToaster();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleContinueFromModal = async () => {
    setIsModalOpen(false);
    setIsLoading(true);
  
    try {
      const { id: channelId, service } = channel;
      const { id: accountId } = account;
  
      if (!service || !channelId || !accountId) {
        throw new Error("Missing required channel or account information");
      }
  
      if (service === "instagram") {
        // Instagram: Validate permissions
        const apiUrl = `/accounts/${accountId}/channels/${channelId}/validate-permissions`;
  
        const response = await callApi({
          method: "post",
          url: apiUrl,
          data: {
            service,
          },
        });
    
        if (response.success) {
          addToast(
            "Instagram permissions were successfully validated!",
            "success"
          );
        } else if (response.missingPermissions && response.missingPermissions.length > 0) {
          addToast(
            `Missing permissions: ${response.missingPermissions.join(", ")}`,
            "error"
          );
        } else {
          addToast("An error occurred while validating permissions.", "error");
        }
      } else if (
        service === "facebook" ||
        service === "twitter" ||
        service === "linkedin"
      ) {
        // For Facebook, Twitter, LinkedIn: Immediate reconnection
        onConnect();
      }
    } catch (error) {
      console.error("Error during API call:", error);
      addToast("An error occurred. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = () => {
    setIsModalOpen(true);
  };
    const closeModal = () => {
      setIsModalOpen(false);
    };

  let button = null;

  switch (status) {
    case "connected":
      button = null;
      break;
    case "toConnect":
      button = (
        <Button variant="primary" onClick={onConnect} disabled={disabled}>
          Connect
        </Button>
      );
      break;
    case "toUpgrade":
      button = (
        <Button
          variant="primaryLite"
          onClick={() =>
            history.push(`/accounts/${account.id}/settings/billing#usage`)
          }
        >
          Upgrade
        </Button>
      );
      break;
    case "toReconnect":
      button = <Button onClick={onConnect}>Reconnect</Button>;
      break;
      case "toVerify":
        if (channel.service === "twitter" || channel.service === "linkedin" || channel.service === "facebook") {
          button = (
            <>
              <Button
                onClick={() =>
                  window.open("https://www.willow.co/customer-success-and-support/insufficient-permissions", "_blank")
                }
              >
                1. Add Permissions
              </Button>
              <Button
                variant="secondary"
                onClick={handleButtonClick}
                disabled={isLoading || disabled}
              >
                2. Reconnect
              </Button>
            </>
          );
        } else if (channel.service === "instagram") {
          button = (
            <>
              <Button
                onClick={() =>
                  window.open("https://www.willow.co/customer-success-and-support/insufficient-permissions", "_blank")
                }
              >
                1. Add Permissions
              </Button>
              <Button
                variant="secondary"
                onClick={handleButtonClick}
                disabled={isLoading || disabled}
              >
                2. Sync
              </Button>
            </>
          );
        }
        break;
    case "toUrgentlyReconnect":
      button = (
        <Button variant="danger" onClick={onConnect} icon={Warning}>
          Reconnect
        </Button>
      );
      break;
    default:
      break;
  }

  let channelAvatar;
  if (
    status === "connected" ||
    status === "toReconnect" ||
    status === "toVerify" ||
    status === "toUrgentlyReconnect"
  ) {
    channelAvatar = (
      <>
        <Avatar
          src={avatar ? avatar : DefaultAvatar}
          $isRounded
          width={48}
          height={48}
          $noShadow
          onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = DefaultAvatar;
          }}
        />
        <Service $size={24} title={channel.username} $type={channel.service} />
      </>
    );
  } else {
    channelAvatar = (
      <>
        <Avatar
          enabled={true}
          src={squareIcon}
          width={48}
          height={48}
          $isRounded
          $noShadow
        />
      </>
    );
  }
  let channelStatus;
  switch (status) {
    case "toReconnect":
      channelStatus = "Disconnected";
      break;
    case "toVerify":
      channelStatus = "Insufficient Permissions";
      break;
    case "toUrgentlyReconnect":
      channelStatus = "Reconnection required";
      break;
    case "readOnly":
      channelStatus = "Read-only";
      break;
    default:
      channelStatus = "Connected";
      break;
  }

  return (
    <ServiceCard status={status} connected={connected}>
      <Flex px={32} py={20} alignItems="center" justifyContent="space-between">
        <Box my={20}>
          <ChannelAvatar
            $clickable={
              !status ||
              status === "toConnect" ||
              status === "toReconnect" ||
              status === "toVerify" ||
              status === "toUrgentlyReconnect"
            }
            $enabled={true}
            onClick={
              !status ||
              status === "toConnect" ||
              status === "toReconnect" ||
              status === "toVerify" ||
              status === "toUrgentlyReconnect"
                ? onConnect
                : null
            }
          >
            {channelAvatar}
          </ChannelAvatar>
        </Box>
        <ServiceName>
          {username && <Username>{username}</Username>}
          {(status === "connected" ||
            status === "toReconnect" ||
            status === "toVerify" ||
            status === "toUrgentlyReconnect") && (
            <ChannelStatus status={status}>{channelStatus}</ChannelStatus>
          )}
          {status !== "connected" &&
            status !== "toReconnect" &&
            status !== "toVerify" &&
            status !== "toUrgentlyReconnect" &&
            instruction && <Instruction>{instruction}</Instruction>}
        </ServiceName>
        <CardActionWrapper>
          {button}
          {connected && onDisconnect && (
            <CardHeader showMore={true} onDisconnect={onDisconnect} />
          )}
        </CardActionWrapper>
      </Flex>
      <PermissionsAddedModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        onContinue={handleContinueFromModal}
      />      
      </ServiceCard>
  );
};

function CardHeader({ showMore, onDisconnect }) {
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuClosingTimeout, setMenuClosingTimeout] = useState(0);

  const closeMenu = () => {
    setMenuVisible(false);
    clearTimeout(menuClosingTimeout);
  };

  const showMenu = () => {
    clearTimeout(menuClosingTimeout);
    const timeout = setTimeout(closeMenu, 1000);
    setMenuClosingTimeout(timeout);
    setMenuVisible(true);
  };

  const preventClosing = () => {
    clearTimeout(menuClosingTimeout);
  };

  return (
    <CardHead height={40} width={40} p={10}>
      {showMore && (
        <CardMoreMenu
          height={20}
          width={20}
          onClick={menuVisible ? closeMenu : showMenu}
        >
          <Image src={moreIcon} alt="More icon" />
        </CardMoreMenu>
      )}{" "}
      &nbsp;
      <CardActions
        style={{ display: menuVisible ? "block" : "none" }}
        onMouseEnter={preventClosing}
        onMouseLeave={closeMenu}
      >
        <CardAction onClick={onDisconnect}>
          <Flex alignItems="center">
            <span className="icon-delete"></span>
            Remove this account
          </Flex>
        </CardAction>
      </CardActions>
    </CardHead>
  );
}

const mapStateToProps = state => {
  return {
    account: state.account.data,
    currentUser: state.auth.currentUser
  };
};

export default connect(mapStateToProps, {})(ChannelConnectionCard);
